import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { Table } from 'components/Table/components/Table';
import type { FC } from 'react';
import { useExtensionsPage } from './useExtensionsPage';

import { Button } from 'components/Button/Button';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { PermissionsRequiredPage } from 'pages/PermissionsRequired/PermissionsRequiredPage';
import React, { useEffect, useState } from 'react';
import { fetchExtensions } from '../model/commands/fetchExtensions';
import { ExtensionsDialog } from './ExtensionsDialog/ExtensionsDialog';
import { useExtensionsTableColumns } from './useExtensionsTableColumns';

const AddExtensionButton = React.memo(
  ({
    onClick,
    children,
  }: { onClick: () => void; children: React.ReactNode }) => {
    return (
      <Button
        variant='secondary'
        onClick={onClick}
        type='button'
        icon='fa-plus'
        size='xs'
      >
        {children}
      </Button>
    );
  },
);

export const ExtensionsPage: FC = () => {
  const [openEditExtensionSidePanel, setOpenDetailExtensionSidePanel] =
    useState(false);
  const { actions, model, permissions } = useExtensionsPage();
  const interactionsConfirmDialog = useSimpleConfirmDialog({
    btnConfirmAccent: 'positive',
    btnCancelAccent: 'negative',
  });
  const removalConfirmDialog = useSimpleConfirmDialog();
  const { columns, data } = useExtensionsTableColumns({
    model,
    interactionsConfirmDialog,
    removalConfirmDialog,
  });

  useEffect(() => {
    if (permissions.canFetchExtensions) {
      model.dispatch(fetchExtensions);
    }
  }, [model.dispatch, permissions.canFetchExtensions]);

  if (!permissions.canFetchExtensions) {
    return <PermissionsRequiredPage />;
  }

  if (model.extensionsLoading) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <LoaderAndText />
      </div>
    );
  }

  return (
    <PageLayout className='flex flex-col'>
      <>
        {permissions.canRegisterExtension && (
          <div className='row'>
            <div className='col-xs-12 col-md-6'>
              <AddExtensionButton
                onClick={() => setOpenDetailExtensionSidePanel(true)}
              >
                Add
              </AddExtensionButton>
            </div>
          </div>
        )}
        {openEditExtensionSidePanel && (
          <ExtensionsDialog
            onClose={() => setOpenDetailExtensionSidePanel(false)}
            onSubmit={() => model.dispatch(fetchExtensions)}
            logsRefreshing={false}
            platformUsers={model.platformUsers}
          />
        )}
        {model.readonlyExtensionDialogOpen && (
          <ExtensionsDialog
            onClose={actions.closeExtensionDetailsDialog}
            platformUsers={model.platformUsers}
            initialValues={model.extensionDetails?.extension}
            logs={model.extensionDetails?.logs}
            logsRefreshing={model.extensionDetails?.logsRefreshing}
            onRefreshLogs={actions.refreshExtensionLogs}
            isReadonly={true}
          />
        )}
        <Table
          borderless
          className='-mx -mb mt flex-1 border-t border-default rounded-b'
          columns={columns}
          data={data}
          rowIdField='id'
          title={'Extensions'}
        />
        {interactionsConfirmDialog.render()}
        {removalConfirmDialog.render()}
      </>
    </PageLayout>
  );
};
